import { useCallback, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useUploadStoryMutation } from 'api/stories/stories'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { useModalContext } from 'context/modalContext'
import { useUserProfile } from 'store'
import { type NewStory } from 'types/api/story'

interface ShareStoryProps {
  story: NewStory
  onSuccess?: () => void
}

export function useShareStory({ story, onSuccess }: ShareStoryProps) {
  const { t } = useTranslation()
  const userData = useUserProfile()!
  const queryClient = useQueryClient()
  const modalData = useModalContext()

  const { mutate: uploadStory, isLoading } = useUploadStoryMutation()

  const inFlightRef = useRef(false)

  const handleShareClick = useCallback(() => {
    if (userData.status === 'restricted') {
      addToast('error', t('storyRestrictedAcc'))
      return
    }

    if (isLoading || inFlightRef.current) return

    inFlightRef.current = true

    uploadStory(story, {
      onSuccess: () => {
        onSuccess?.()
        queryClient.invalidateQueries('allStoryPosts')
      },

      onError: (error: any) => {
        const code = error?.response?.data?.code
        const message = error?.response?.data?.message

        if (code === 'STORY_LIMIT_REACHED') {
          modalData.addModal(
            t('storyLimitReached'),
            <ConfirmModal
              body={
                message ??
                'You can only post 10 stories in a 24 hour period.'
              }
              confirmText={t('OK')}
              confirmFn={() => {}}
              hideCancel
            />
          )
        }
      },

      onSettled: () => {
        inFlightRef.current = false
      },
    })
  }, [
    userData.status,
    t,
    isLoading,
    uploadStory,
    story,
    onSuccess,
    queryClient,
    modalData,
  ])

  return {
    handleShareClick,
    isSubmitting: isLoading || inFlightRef.current,
  }
}